/**
 * Keeps a target-driven modal mounted through its close animation.
 *
 * `{target && <Modal target={target} …/>}` unmounts the modal on the same tick
 * the target is cleared, so Radix never runs the cleanup that releases the body
 * lock: the modal vanishes and the page behind it stops taking clicks until a
 * reload. Conditional mounting is nonetheless the natural way to write a modal
 * whose form state is seeded from the row it was opened for — the component
 * wants a non-null target for its hook initialisers.
 *
 * ClosingGate keeps both: it renders while it holds a target, reports `open` as
 * false once the caller's target is gone, and drops the child only after the
 * close has run. Each new target gets a fresh mount, so seeded state is still
 * per-row.
 *
 *   <ClosingGate value={editTarget} onCleared={() => setEditTarget(null)}>
 *     {(user, open, onOpenChange) => (
 *       <EditUserModal user={user} open={open} onOpenChange={onOpenChange} />
 *     )}
 *   </ClosingGate>
 *
 * The child must drive its own dialog from the `open` it is handed, and call
 * `onOpenChange(false)` to close.
 */

'use client';

import * as React from 'react';

/** Long enough for Radix's close animation; it is a ceiling, not a delay. */
const CLOSE_GRACE_MS = 400;

export function ClosingGate<T>({
  value,
  onCleared,
  children,
}: {
  /** The caller's target. Non-null opens; null starts the close. */
  value: T | null | undefined;
  /** Runs once the child has closed and been dropped. */
  onCleared?: () => void;
  children: (value: T, open: boolean, onOpenChange: (open: boolean) => void) => React.ReactNode;
}) {
  // The last non-null target, held so the child keeps rendering while closing.
  const [held, setHeld] = React.useState<T | null>(value ?? null);
  const timer = React.useRef<ReturnType<typeof setTimeout> | null>(null);

  React.useEffect(() => {
    if (timer.current) {
      clearTimeout(timer.current);
      timer.current = null;
    }

    if (value != null) {
      setHeld(value);
      return undefined;
    }

    if (held == null) return undefined;

    timer.current = setTimeout(() => {
      timer.current = null;
      setHeld(null);
    }, CLOSE_GRACE_MS);

    return () => {
      if (timer.current) clearTimeout(timer.current);
      timer.current = null;
    };
    // `held` is written here, not read as a trigger: reacting to it would
    // re-arm the timer on the very state change it schedules.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [value]);

  const handleOpenChange = React.useCallback(
    (open: boolean) => {
      if (open) return;
      onCleared?.();
    },
    [onCleared],
  );

  if (held == null) return null;
  // `value == null` means the caller has closed it — the child stays mounted and
  // is told it is closed, so Radix can animate out and clean up after itself.
  return <>{children(held, value != null, handleOpenChange)}</>;
}
